import type { HTMLAttributes } from 'react';
import { cx } from '../../utils/cx.js';
import type { DataTableVariant } from './dataTableVariant.js';
import styles from './DataTable.module.css';

export type DataTableScrollMode = 'x' | 'none';
export type DataTableLayoutMode = 'paginated' | 'fill' | 'embedded';
export type DataTableDensity = 'default' | 'compact' | 'comfortable';

type DataTableRootAttrs = HTMLAttributes<HTMLDivElement> & {
  'data-wp-datatable-root': true;
  'data-wp-datatable-scroll': DataTableScrollMode;
  'data-wp-datatable-density': DataTableDensity;
  'data-wp-datatable-layout': DataTableLayoutMode;
  'data-wp-datatable-variant'?: DataTableVariant;
  'data-wp-datatable-resizable'?: '';
  'data-wp-datatable-col-resizing'?: '';
  'data-wp-datatable-row-scroll'?: '';
  'data-wp-datatable-sticky-actions'?: '';
  'data-wp-datatable-sticky-header'?: '';
  'data-wp-datatable-selection-active'?: '';
  'data-wp-proximity'?: 'on';
};

export function getDataTableRootProps(options: {
  scroll: DataTableScrollMode;
  density: DataTableDensity;
  layout: DataTableLayoutMode;
  resizable?: boolean;
  resizing?: boolean;
  rowScrollDrag?: boolean;
  narrow?: boolean;
  empty?: boolean;
  compactEmpty?: boolean;
  fillRegion?: boolean;
  serverFillRegion?: boolean;
  viewportFitRegion?: boolean;
  virtualScrollRegion?: boolean;
  heightCapRegion?: boolean;
  stickyActionsEnd?: boolean;
  variant?: DataTableVariant;
  selectionActive?: boolean;
  proximityHover?: boolean;
  className?: string;
  tabIndex?: number;
  'aria-label'?: string;
  style?: HTMLAttributes<HTMLDivElement>['style'];
}): DataTableRootAttrs {
  const stickyHeaderRow = Boolean(
    options.serverFillRegion ||
      options.viewportFitRegion ||
      options.virtualScrollRegion ||
      options.heightCapRegion,
  );
  return {
    className: cx(
      styles.tableBodyRegion,
      options.fillRegion && styles.tableBodyRegionFill,
      options.serverFillRegion && styles.tableBodyRegionServerFill,
      options.viewportFitRegion && styles.tableBodyRegionViewportFit,
      options.virtualScrollRegion && styles.tableBodyRegionVirtualScroll,
      options.heightCapRegion && styles.tableBodyRegionHeightCap,
      options.scroll === 'none' && styles.tableBodyRegionNoHScroll,
      options.empty && styles.tableBodyRegionHasEmpty,
      options.empty && !options.compactEmpty && styles.tableBodyRegionWithOverlay,
      options.empty && options.compactEmpty && styles.tableBodyRegionCompactEmpty,
      options.className,
    ),
    'data-wp-datatable-root': true,
    'data-wp-datatable-scroll': options.scroll,
    'data-wp-datatable-density': options.density,
    'data-wp-datatable-layout': options.layout,
    ...(options.variant && options.variant !== 'list'
      ? { 'data-wp-datatable-variant': options.variant }
      : {}),
    ...(options.resizable ? { 'data-wp-datatable-resizable': '' } : {}),
    ...(options.resizing ? { 'data-wp-datatable-col-resizing': '' } : {}),
    ...(options.rowScrollDrag ? { 'data-wp-datatable-row-scroll': '' } : {}),
    ...(options.narrow ? { 'data-wp-datatable-narrow': '' } : {}),
    ...(options.stickyActionsEnd ? { 'data-wp-datatable-sticky-actions': '' } : {}),
    ...(stickyHeaderRow ? { 'data-wp-datatable-sticky-header': '' } : {}),
    ...(options.selectionActive ? { 'data-wp-datatable-selection-active': '' } : {}),
    ...(options.proximityHover ? { 'data-wp-proximity': 'on' } : {}),
    ...(options.tabIndex != null ? { tabIndex: options.tabIndex } : {}),
    ...(options['aria-label'] ? { 'aria-label': options['aria-label'] } : {}),
    ...(options.style ? { style: options.style } : {}),
  } as DataTableRootAttrs;
}

export function getDataTableShellProps(options: {
  layout: DataTableLayoutMode;
  fillHeight?: boolean;
  serverFill?: boolean;
  viewportFit?: boolean;
  stickyActionsEnd?: boolean;
  className?: string;
}): HTMLAttributes<HTMLDivElement> & {
  'data-wp-datatable-shell': true;
  'data-wp-datatable-layout': DataTableLayoutMode;
  'data-wp-datatable-sticky-actions'?: '';
} {
  return {
    className: cx(
      styles.tableShell,
      options.fillHeight && styles.tableShellFill,
      options.serverFill && styles.tableShellServerFill,
      options.viewportFit && styles.tableShellViewportFit,
      options.className,
    ),
    'data-wp-datatable-shell': true,
    'data-wp-datatable-layout': options.layout,
    ...(options.stickyActionsEnd ? { 'data-wp-datatable-sticky-actions': '' } : {}),
  } as HTMLAttributes<HTMLDivElement> & {
    'data-wp-datatable-shell': true;
    'data-wp-datatable-layout': DataTableLayoutMode;
    'data-wp-datatable-sticky-actions'?: '';
  };
}
